import { addCalendarDays } from "./schemas/meal-plan.js";
function contributesToShopping(slot) {
    return slot.skipped !== true && slot.leftoverOf === undefined;
}
/** Unique recipeIds → number of separate (non-leftover, non-skipped) cooks this week. */
export function collectPlanRecipeIds(plan) {
    const counts = new Map();
    for (const day of plan.days) {
        for (const slot of day.slots) {
            if (!contributesToShopping(slot))
                continue;
            for (const item of slot.items) {
                if (item.kind !== "recipe")
                    continue;
                counts.set(item.recipeId, (counts.get(item.recipeId) ?? 0) + 1);
            }
        }
    }
    return counts;
}
function slotDisplayTitle(slot, opts) {
    const first = slot.items[0];
    if (!first)
        return null;
    if (first.kind === "recipe")
        return opts?.recipeTitle?.(first.recipeId) ?? null;
    return first.title;
}
/** Index every slot by id for leftover-label resolution (survives moves). */
export function buildSlotIndex(plan) {
    const slotsById = new Map();
    for (const day of plan.days)
        for (const s of day.slots)
            slotsById.set(s.id, s);
    return slotsById;
}
/**
 * Resolve a slot's leftover label: explicit `ref.label` → source-slot title
 * (freetext title, or `opts.recipeTitle(recipeId)` for recipe items) → literal "Leftover".
 * Returns `null` when the slot has no `leftoverOf`.
 */
export function resolveLeftoverLabel(slot, slotsById, opts) {
    const ref = slot.leftoverOf;
    if (!ref)
        return null;
    if (ref.label)
        return ref.label;
    if (ref.slotId) {
        const src = slotsById.get(ref.slotId);
        const t = src ? slotDisplayTitle(src, opts) : null;
        if (t)
            return t;
    }
    return "Leftover";
}
/** Pure view of a plan for a given local calendar `today` (spec §4.4). */
export function deriveTodayDigest(plan, today, opts = {}) {
    const comingUpDays = opts.comingUpDays ?? 3;
    const slotsById = buildSlotIndex(plan);
    const todayDay = plan.days.find((d) => d.date === today);
    const meals = [];
    for (const slot of todayDay?.slots ?? []) {
        if (slot.skipped === true)
            continue;
        const hasContent = slot.items.length > 0 || slot.leftoverOf !== undefined;
        if (!hasContent)
            continue;
        meals.push({
            mealType: slot.mealType,
            slotId: slot.id,
            items: slot.items,
            leftoverLabel: resolveLeftoverLabel(slot, slotsById),
            note: slot.note ?? null,
        });
    }
    const toDigestTask = (t) => ({
        id: t.id, title: t.title, kind: t.kind ?? null,
        startByTime: t.startByTime ?? null, done: t.done,
    });
    const tasksToday = plan.tasks.filter((t) => t.doOnDate === today).map(toDigestTask);
    const startByWarnings = tasksToday.filter((t) => t.startByTime !== null);
    const horizon = addCalendarDays(today, comingUpDays);
    const comingUp = plan.tasks
        .filter((t) => t.doOnDate > today && t.doOnDate <= horizon)
        .sort((a, b) => (a.doOnDate < b.doOnDate ? -1 : a.doOnDate > b.doOnDate ? 1 : 0))
        .map((t) => ({ date: t.doOnDate, title: t.title, kind: t.kind ?? null, startByTime: t.startByTime ?? null }));
    return { today, meals, tasksToday, comingUp, startByWarnings };
}
//# sourceMappingURL=meal-plan-digest.js.map